import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";

import { AssetsService } from "../../../../../services/assets-service";
import { ToastrService } from "../../../../../services/toastr.service";
import { ParentPermissionEnum, PermissionActionEnum } from "../../../../../enums/enums";
import { DownloadService } from "../../../../../services/download.service";
import { formatDate } from "../../../../../common/vue-helpers/modifiers/value-modifier";
import { PermissionManager } from "../../../../../common/utilities/permission-manager";
import {
    IGetVisitDocumentResponse,
    IUploadVisitDocumentResponse
} from "../../../../../resources-vue/vue-interfaces/i-asset";
import { TaskService } from "../../../../../services/task.service";
import {
    IDeleteTaskAttachmentRequest,
    IGetAllTaskAttachmentsRequest,
    ITaskAttachement
} from "../../../../../resources-vue/vue-interfaces/i-task";
import { IBlobResponse } from "../../../../../resources-vue/vue-interfaces/i-hospice-client";

import TableEmpty from "../../../../../resources-vue/vue-custom-elements/TableEmpty/TableEmpty.vue";
import TypeaheadInput from "../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";
import AddAttachmentsFormHosPal from "./AddAttachmentsFormHosPal/AddAttachmentsFormHosPal.vue";
import TablePrompt from "../../../../../resources-vue/vue-custom-elements/TablePrompt/TablePrompt.vue";

export default defineComponent({
    components: {
        AddAttachmentsFormHosPal,
        TableEmpty,
        TypeaheadInput,
        TablePrompt
    },

    props: {
        visitDetails: { type: Object },
        isPlanner: { type: Boolean, default: false }
    },

    data() {
        return {
            fileDetails: [] as IUploadVisitDocumentResponse[],
            documentToDelete: null as string,
            taskAttchments: [] as ITaskAttachement[],
            loadingCount: 3,
            isDeleteInProgress: false,
            uploadFile: false,
            addPatientDocumentsPermission: false,
            editPatientDocumentsPermission: false,
            deletePatientDocumentsPermission: false,
            loadingDocuments: false,
            _taskService: null as TaskService,
            _assetsService: null as AssetsService,
            _downloadService: null as DownloadService,
            _permissionManager: null as PermissionManager,
            _toastrService: null as ToastrService,
            formatDate
        };
    },

    created() {
        this._assetsService = Container.instance.get(AssetsService);
        this._taskService = Container.instance.get(TaskService);
        this._toastrService = Container.instance.get(ToastrService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._downloadService = Container.instance.get(DownloadService);
    },

    async mounted() {
        this.addPatientDocumentsPermission = this._permissionManager.checkPermission(
            ParentPermissionEnum.referrals,
            PermissionActionEnum.canAdd,
            this.visitDetails?.AgencyId
        );
        this.deletePatientDocumentsPermission = this._permissionManager.checkPermission(
            ParentPermissionEnum.referrals,
            PermissionActionEnum.canDelete,
            this.visitDetails?.AgencyId
        );
        this.editPatientDocumentsPermission = this._permissionManager.checkPermission(
            ParentPermissionEnum.referrals,
            PermissionActionEnum.canEdit,
            this.visitDetails?.AgencyId
        );
        await this.loadAttchments();
    },

    methods: {
        requestDelete(documentId: string) {
            this.documentToDelete = documentId;
        },
        cancelDeleteAttachment() {
            this.documentToDelete = null;
        },
        async deleteSelectedDocument() {
            try {
                const payload: IDeleteTaskAttachmentRequest = {
                    taskId: this.visitDetails?.Id,
                    agencyId: this.visitDetails?.AgencyId,
                    id: this.documentToDelete,
                    application: this.visitDetails?.Application,
                    userId: this.visitDetails?.UserId
                };
                this.isDeleteInProgress = true;
                await this._taskService.deleteDocument(payload);
                this.isDeleteInProgress = false;
                this.loadAttchments();
                this._toastrService.success({
                    message: `The document has been successfully deleted.`,
                    title: `Document Deleted`
                });
            } catch (error) {
                console.error(error);
                this.isDeleteInProgress = false;
                this._toastrService.error({
                    title: `Delete Document Failed`,
                    message: `Delete document failed, Please contact axxess if issue still persists.`
                });
            }
        },

        async downloadDocuments(document: IGetVisitDocumentResponse) {
            await this._assetsService
                .getVisitDocumentBlobByUrl(document.Url)
                .then((response: IBlobResponse) => {
                    this._downloadService.downloadFileWithFileName(response, document.MimeType, document.DocumentName);
                    this._toastrService.success({
                        message: `The requested document is downloaded.`
                    });
                })
                .catch((error: any) => {
                    console.error(error);
                    this._toastrService.error({
                        title: `Downloading Document Failed`,
                        message: `Downloading document failed, Please contact axxess if issue still persists.`
                    });
                });
        },

        async openSelectedDocument(document: IGetVisitDocumentResponse) {
            return await this._assetsService
                .getVisitDocumentBlobByUrl(document.Url)
                .then((response: IBlobResponse) => {
                    const url = window.URL.createObjectURL(response.body as Blob);
                    window.open(url, "_blank");
                    this._toastrService.success({
                        message: `The requested document is downloaded.`
                    });
                })
                .catch((error: any) => {
                    console.error(error);
                    this._toastrService.error({
                        title: `Opening Document Failed`,
                        message: `Opening document failed, Please contact axxess if issue still persists.`
                    });
                });
        },

        async loadAttchments() {
            this.loadingDocuments = true;
            try {
                const payload: IGetAllTaskAttachmentsRequest = {
                    application: this.visitDetails?.Application,
                    accountId: this.visitDetails?.AgencyId,
                    taskId: this.visitDetails?.Id
                };
                this.taskAttchments = await this._taskService.fetchAllTaskAttachements(payload);
            } catch (error) {
                console.error(error);
                this._toastrService.error({
                    title: `Error`,
                    message: `Error loading attachments, Please contact axxess if issue still persists.`
                });
            } finally {
                this.loadingDocuments = false;
            }
        },

        toggleUploadFile() {
            this.uploadFile = !this.uploadFile;
        }
    }
});
